import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Student, Task } from '@/types';
import { TaskSummary } from './TaskSummary';
import { TaskGrid } from './TaskGrid';
import { TaskForm } from './TaskForm';

interface TasksSectionProps {
  values: Partial<Student>;
  onChange: (id: keyof Student, value: any) => void;
}

export const TasksSection: React.FC<TasksSectionProps> = ({
  values,
  onChange
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const tasks = values.tasks || [];
  const filteredTasks = statusFilter
    ? tasks.filter(task => task.status === statusFilter)
    : tasks;

  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setIsFormOpen(true);
  };

  const handleSaveTask = (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    const now = new Date().toISOString();
    
    if (selectedTask) {
      // Update existing task
      const updatedTasks = tasks.map(task => 
        task.id === selectedTask.id ? {
          ...task,
          ...taskData,
          updatedAt: now
        } : task
      );
      onChange('tasks', updatedTasks);
    } else {
      // Create new task
      const newTask = {
        ...taskData,
        id: `task-${Date.now()}`,
        assignedTo: values.id || '',
        createdAt: now,
        updatedAt: now,
        comments: [],
        reminders: [],
        attachments: []
      };
      onChange('tasks', [...tasks, newTask]);
    }
    
    setIsFormOpen(false);
    setSelectedTask(null);
  };

  const handleDeleteTask = (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      onChange('tasks', tasks.filter(t => t.id !== taskId));
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h2">
            Student Tasks
            <Typography 
              component="span" 
              variant="h5" 
              color="text.secondary" 
              sx={{ ml: 1, fontWeight: 'normal' }}
            >
              ({tasks.length})
            </Typography>
          </Typography>
        </Box>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => {
            setSelectedTask(null);
            setIsFormOpen(true);
          }}
        >
          Add Task
        </Button>
      </Stack>

      <TaskSummary 
        tasks={tasks} 
        onFilterByStatus={setStatusFilter}
        activeFilter={statusFilter}
      />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TaskGrid
            tasks={filteredTasks}
            onEdit={handleEditTask}
            onDelete={handleDeleteTask}
          />
        </Grid>
      </Grid>

      <TaskForm
        open={isFormOpen}
        task={selectedTask}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedTask(null);
        }}
        onSave={handleSaveTask}
      />
    </Box>
  );
};